import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SkuPicker } from "@/components/containers/SkuPicker";
import { Scissors, Trash2, Undo2, Merge } from "lucide-react";

export const allocText = (alloc) => (alloc || []).map((a) => `${a.pallet}:${a.boxes}`).join(", ");

const Declared = ({ r }) => {
  const o = r.origin;
  if (r.orig) {
    const d = r.orig.declared;
    return (
      <div>
        <span className="font-mono">{d.sku}</span> · <strong>{d.boxes}</strong>
        {d.rows.length > 0 && <p className="text-[10px] text-warmgrey">{d.rows.map((x) => `row ${x.row}${x.pallet != null ? ` · pallet ${x.pallet}` : ""}${x.supplier_sku && x.supplier_sku !== d.sku ? ` · supplier "${x.supplier_sku}"` : ""}`).join("; ")}</p>}
      </div>
    );
  }
  if (o === "EXCLUDED_ROW") return <span>Excluded row {r.ex?.row} · <span className="font-mono">{r.ex?.supplier_sku}</span> ({r.ex?.boxes})</span>;
  if (o === "SPLIT") return <span>Split from <span className="font-mono">{r.splitSku}</span></span>;
  return <span className="text-warmgrey">{o === "OVERAGE" ? "Overage — not declared" : "Not declared"}</span>;
};

const num = (v) => (v === "" || v == null ? "" : Number(v));

export const ReconcileRow = ({ r, received, reasons, canMerge, onChange, onSplit, onMergeStart }) => {
  const set = (patch) => onChange({ ...r, ...patch });
  const t = `recon-${r.key}`;
  const qty = received ? num(r.rec?.boxes_received) : num(r.expected);
  const base = received ? num(r.expected) : (r.orig ? r.orig.declared.boxes : 0);
  const diff = r.remove || r.merge_into ? null : (qty === "" ? null : qty - (base === "" ? 0 : base));
  const off = r.remove || !!r.merge_into;
  return (
    <tr className={`border-b border-border align-top text-xs ${off ? "bg-alabaster text-warmgrey line-through" : ""}`} data-testid={t}>
      <td className="py-2 pr-2"><SkuPicker value={r.sku} testid={`${t}-sku`} onPick={(v) => set({ sku: v.sku })} /></td>
      <td className="py-2 pr-2"><Declared r={r} /></td>
      <td className="py-2 pr-2"><Input type="number" min={0} disabled={off} value={r.expected} onChange={(e) => set({ expected: e.target.value })} className="h-8 w-20 text-xs" data-testid={`${t}-expected`} /></td>
      {received && (
        <td className="py-2 pr-2">
          <div className="flex gap-1">
            <Input type="number" min={0} disabled={off} value={r.rec?.boxes_received ?? ""} onChange={(e) => set({ rec: { ...r.rec, boxes_received: e.target.value } })} className="h-8 w-16 text-xs" title="Received" data-testid={`${t}-received`} />
            <Input type="number" min={0} disabled={off} value={r.rec?.damaged ?? 0} onChange={(e) => set({ rec: { ...r.rec, damaged: e.target.value } })} className="h-8 w-14 text-xs" title="Damaged" data-testid={`${t}-damaged`} />
          </div>
        </td>
      )}
      <td className={`py-2 pr-2 font-medium ${diff > 0 ? "text-[#2f6b45]" : diff < 0 ? "text-status-sold" : "text-charcoal"}`} data-testid={`${t}-diff`}>{diff == null ? "—" : diff > 0 ? `+${diff}` : diff}</td>
      <td className="py-2 pr-2"><Input disabled={off} value={r.pallets} placeholder="e.g. 18 or 18:40, 19:2" onChange={(e) => set({ pallets: e.target.value, palletsEdited: true })} className="h-8 w-28 text-xs" data-testid={`${t}-pallets`} /></td>
      <td className="py-2 pr-2">
        <select value={r.reason} onChange={(e) => set({ reason: e.target.value })} className="h-8 w-36 rounded-md border border-border bg-white px-1 text-xs" data-testid={`${t}-reason`}>
          <option value="">Reason…</option>
          {Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input value={r.note} onChange={(e) => set({ note: e.target.value })} placeholder="Note" className="mt-1 h-7 w-36 text-[11px]" data-testid={`${t}-note`} />
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {off ? (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => set({ remove: false, merge_into: null })} data-testid={`${t}-undo`}><Undo2 className="h-3.5 w-3.5" /></Button>
        ) : (
          <>
            {r.line_id && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Split line" onClick={onSplit} data-testid={`${t}-split`}><Scissors className="h-3.5 w-3.5" /></Button>}
            {r.line_id && canMerge && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Merge into the other line with this SKU" onClick={onMergeStart} data-testid={`${t}-merge`}><Merge className="h-3.5 w-3.5" /></Button>}
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-warmgrey hover:text-destructive" title="Remove line" onClick={() => (r.line_id ? set({ remove: true }) : onChange(null))} data-testid={`${t}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </>
        )}
      </td>
    </tr>
  );
};
